import { useCallback, useEffect, useState } from "react";
import { UserRound, Mail, Phone, Building2, MapPin, Globe, FileText, AlertTriangle, ShieldCheck, Save, Landmark, Eye, EyeOff, Lock, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PartnerShell } from "./Dashboard";

/**
 * Feb 2026 — Partner-facing self-serve profile page.
 *
 * Scope (locked in with user):
 *   Editable       : contact_name, phone, business_name, contact email
 *                    (partner.email, NOT the login email),
 *                    franchise_region, website, notes.
 *   Read-only ctx  : login email + login full_name (from the users
 *                    row), partner_slug/state/status. Login email is
 *                    displayed with a warning if it differs from the
 *                    partner contact email.
 *   Out of scope   : role, wholesale_tier, partner_type,
 *                    partner_status, permissions, portal login email.
 *                    Those live on the admin side.
 */

function LabeledInput({ id, label, icon: Icon, value, onChange, type = "text", placeholder, hint, testid }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </label>
      <input
        id={id}
        data-testid={testid}
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
      />
      {hint && <p className="mt-1 text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function ReadOnlyRow({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div data-testid={testid} className="text-sm font-mono text-slate-900 mt-0.5 break-all">{value || "—"}</div>
    </div>
  );
}

export default function PartnerProfile() {
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/partner/me/profile");
      setInitial(data);
      setForm({
        contact_name:     data.contact_name || "",
        phone:            data.phone || "",
        business_name:    data.business_name || "",
        email:            data.email || "",
        franchise_region: data.franchise_region || "",
        website:          data.website || "",
        notes:            data.notes || "",
      });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Compare each field against the pristine copy; only send diffs so
  // the audit trail on the partner row stays clean.
  const diff = () => {
    if (!initial || !form) return {};
    const out = {};
    for (const k of Object.keys(form)) {
      const orig = (initial[k] ?? "").toString();
      const cur = (form[k] ?? "").toString();
      if (orig !== cur) out[k] = cur;
    }
    return out;
  };

  const dirty = Object.keys(diff()).length > 0;

  const save = async () => {
    const payload = diff();
    if (!payload.contact_name && !initial?.contact_name) {
      toast.error("Contact name is required.");
      return;
    }
    if (!payload.business_name && !initial?.business_name) {
      toast.error("Business name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/partner/me/profile", payload);
      toast.success("Profile updated.");
      setInitial(data);
      setForm({
        contact_name:     data.contact_name || "",
        phone:            data.phone || "",
        business_name:    data.business_name || "",
        email:            data.email || "",
        franchise_region: data.franchise_region || "",
        website:          data.website || "",
        notes:            data.notes || "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (!initial) return;
    setForm({
      contact_name:     initial.contact_name || "",
      phone:            initial.phone || "",
      business_name:    initial.business_name || "",
      email:            initial.email || "",
      franchise_region: initial.franchise_region || "",
      website:          initial.website || "",
      notes:            initial.notes || "",
    });
  };

  const emailsDiverge =
    !!initial?.login_email &&
    !!form?.email &&
    initial.login_email.toLowerCase() !== form.email.toLowerCase();

  return (
    <PartnerShell active="profile">
      <div className="max-w-3xl mx-auto">
        <header className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-600">Trade Partner</div>
          <h1 className="text-3xl font-bold text-slate-900 mt-1" data-testid="partner-profile-heading">Profile</h1>
          <p className="mt-1">
            <a href="/account/sign-in-methods" data-testid="partner-signin-methods-link" className="text-xs font-semibold text-cyan-700 hover:underline">
              Manage sign-in methods (Google / Facebook / password) →
            </a>
          </p>
          <p className="text-sm text-slate-500 mt-1">
            Update the contact details we display to your customers and use to reach you.
            Role, wholesale tier and partner status are managed by Aware admin.
          </p>
        </header>

        {loading ? (
          <div data-testid="partner-profile-loading" className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
            Loading your profile…
          </div>
        ) : error ? (
          <div data-testid="partner-profile-error" className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-700">
            {error}
          </div>
        ) : (
          <div className="space-y-6" data-testid="partner-profile-form">
            {/* ─── Editable card ─────────────────────────────────────── */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5">
              <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
                <UserRound className="w-4 h-4 text-cyan-600" /> Contact information
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <LabeledInput
                  id="contact_name" label="Contact name" icon={UserRound}
                  value={form.contact_name} onChange={set("contact_name")}
                  placeholder="Your full name"
                  testid="partner-profile-contact-name"
                />
                <LabeledInput
                  id="phone" label="Phone" icon={Phone}
                  value={form.phone} onChange={set("phone")}
                  placeholder="+61 400 000 000"
                  testid="partner-profile-phone"
                />
                <LabeledInput
                  id="business_name" label="Business / franchisee name" icon={Building2}
                  value={form.business_name} onChange={set("business_name")}
                  placeholder="Pristine Water Systems — Sunshine Coast"
                  testid="partner-profile-business-name"
                />
                <LabeledInput
                  id="franchise_region" label="Service / franchise region" icon={MapPin}
                  value={form.franchise_region} onChange={set("franchise_region")}
                  placeholder="e.g. Noosa Shire"
                  testid="partner-profile-franchise-region"
                />
                <LabeledInput
                  id="email" label="Contact email" icon={Mail} type="email"
                  value={form.email} onChange={set("email")}
                  placeholder="contact@yourbusiness.com.au"
                  hint="Public-facing contact address shown to customers. Separate from your portal login email."
                  testid="partner-profile-contact-email"
                />
                <LabeledInput
                  id="website" label="Website" icon={Globe}
                  value={form.website} onChange={set("website")}
                  placeholder="https://…"
                  testid="partner-profile-website"
                />
              </div>
              <div>
                <label htmlFor="notes" className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Profile notes / customer instructions
                </label>
                <textarea
                  id="notes"
                  data-testid="partner-profile-notes"
                  value={form.notes}
                  onChange={(e) => set("notes")(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Anything customers or Aware staff should know when contacting you."
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-slate-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none text-sm"
                />
                <p className="mt-1 text-[11px] text-slate-500 text-right">
                  {form.notes.length}/2000
                </p>
              </div>
            </section>

            {/* ─── Read-only login info ─────────────────────────────── */}
            <section className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
              <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-cyan-600" /> Login &amp; account
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <ReadOnlyRow
                  label="Login email"
                  value={initial.login_email}
                  testid="partner-profile-login-email"
                />
                <ReadOnlyRow
                  label="Name on account"
                  value={initial.login_full_name}
                  testid="partner-profile-login-full-name"
                />
                <ReadOnlyRow
                  label="Partner status"
                  value={initial.pws_partner_status}
                  testid="partner-profile-status"
                />
                <ReadOnlyRow
                  label="Partner slug"
                  value={initial.partner_slug}
                  testid="partner-profile-slug"
                />
              </div>
              {emailsDiverge && (
                <div
                  data-testid="partner-profile-email-mismatch-warning"
                  className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-900"
                >
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <div className="font-semibold">Your contact email differs from your login email.</div>
                    <div className="mt-0.5">
                      Login: <span className="font-mono">{initial.login_email}</span> — Contact: <span className="font-mono">{form.email}</span>.
                      Both are fine and this won&apos;t affect your ability to sign in.
                      Contact Aware admin if you need your login email changed.
                    </div>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-500">
                To change your login email, wholesale tier, franchise status, or any billing/permission field, please
                contact Aware admin.
              </p>
            </section>

            {/* ─── Bank details for payouts (Feb 2026 P0 #1) ─── */}
            <PartnerBankDetailsSection />

            {/* ─── Email notifications (Feb 2026 P1) ────────────── */}
            <PartnerNotificationPrefsSection />

            {/* ─── Sticky action bar ────────────────────────────────── */}
            <div
              data-testid="partner-profile-action-bar"
              className="sticky bottom-4 rounded-2xl bg-slate-900 text-white px-5 py-3.5 shadow-xl flex items-center justify-between gap-3"
            >
              <div className="text-xs">
                {dirty ? (
                  <span data-testid="partner-profile-dirty-flag">
                    You have unsaved changes ({Object.keys(diff()).length} field{Object.keys(diff()).length !== 1 ? "s" : ""}).
                  </span>
                ) : (
                  <span>All changes saved.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="partner-profile-revert-btn"
                  onClick={revert}
                  disabled={!dirty || saving}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-40 transition-colors"
                >
                  Revert
                </button>
                <button
                  type="button"
                  data-testid="partner-profile-save-btn"
                  onClick={save}
                  disabled={!dirty || saving}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold bg-cyan-500 text-white hover:bg-cyan-400 disabled:opacity-50 transition-colors"
                >
                  <Save className="w-3.5 h-3.5" />
                  {saving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PartnerShell>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Bank details for payouts (Feb 2026 P0 #1)
// ═══════════════════════════════════════════════════════════════════

const EMPTY_BANK_FORM = {
  account_name:     "",
  bsb:              "",
  account_number:   "",
  payid:            "",
  remittance_email: "",
  notes:            "",
};

function normaliseBsb(s) {
  return (s || "").replace(/\D/g, "").slice(0, 6);
}
function displayBsb(s) {
  const d = normaliseBsb(s);
  return d.length < 6 ? d : `${d.slice(0, 3)}-${d.slice(3)}`;
}

function BankStatusPill({ status }) {
  const map = {
    complete:   { pill: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", label: "Complete" },
    incomplete: { pill: "bg-amber-100 text-amber-800",     dot: "bg-amber-500",   label: "Incomplete" },
    missing:    { pill: "bg-slate-100 text-slate-500",     dot: "bg-slate-400",   label: "Not set up" },
  };
  const s = map[status] || map.missing;
  return (
    <span
      data-testid={`partner-bank-status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${s.pill}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function PartnerBankDetailsSection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_BANK_FORM);
  const [saving, setSaving] = useState(false);
  const [showFull, setShowFull] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/partner/bank-details");
      setState(data);
      if (data.revealed) {
        setForm({
          account_name:     data.revealed.account_name    || "",
          bsb:              data.revealed.bsb             || "",
          account_number:   data.revealed.account_number  || "",
          payid:            data.revealed.payid           || "",
          remittance_email: data.revealed.remittance_email || "",
          notes:            data.revealed.notes           || "",
        });
      } else {
        setForm(EMPTY_BANK_FORM);
      }
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/partner/bank-details", {
        ...form,
        bsb: normaliseBsb(form.bsb),
        account_number: (form.account_number || "").replace(/\D/g, ""),
      });
      toast.success("Bank details saved.");
      setEditing(false);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.field) {
        toast.error(`${detail.field}: ${detail.message}`);
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear your saved bank details? Aware will need new details before your next payout.")) return;
    try {
      await api.delete("/partner/bank-details", { data: {} });
      toast.success("Bank details cleared.");
      setEditing(false);
      setForm(EMPTY_BANK_FORM);
      setShowFull(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <section
      data-testid="partner-bank-details-section"
      className="rounded-2xl border border-slate-200 bg-white p-6"
    >
      <header className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-cyan-600" />
            <h2 className="text-lg font-bold text-slate-900" data-testid="partner-bank-details-heading">
              Bank details for payouts
            </h2>
            {state && <BankStatusPill status={state.status} />}
          </div>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            Where Aware sends your monthly commission payments. Stored encrypted at rest.
            Only you and Aware admin can view these details. Admin views are audit-logged.
          </p>
        </div>
      </header>

      {loading ? (
        <div data-testid="partner-bank-details-loading" className="p-6 text-center text-sm text-slate-500">
          Loading…
        </div>
      ) : error ? (
        <div data-testid="partner-bank-details-error" className="p-4 mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg">
          {error}
        </div>
      ) : editing ? (
        <BankDetailsForm
          form={form}
          setForm={setForm}
          onCancel={() => { setEditing(false); load(); }}
          onSave={save}
          saving={saving}
        />
      ) : state?.has_bank_details ? (
        <div data-testid="partner-bank-details-summary" className="mt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <BankField label="Account name" value={showFull ? state.revealed?.account_name : "•••••"} />
            <BankField label="BSB"           value={state.bsb} />
            <BankField label="Account number" value={showFull ? state.revealed?.account_number : state.account_number_masked} />
            <BankField label="PayID"         value={showFull ? (state.revealed?.payid || "—") : (state.revealed?.payid ? "•••" : "—")} />
            <BankField label="Remittance email" value={showFull ? (state.revealed?.remittance_email || "—") : (state.revealed?.remittance_email ? "•••@•••" : "—")} />
            <BankField label="Notes"         value={showFull ? (state.revealed?.notes || "—") : (state.revealed?.notes ? "•••" : "—")} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Lock className="w-3.5 h-3.5" />
            <span>
              Last updated {new Date(state.last_updated_at).toLocaleString("en-AU")}
              {state.last_updated_by_role === "admin" ? " by Aware admin." : " by you."}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="partner-bank-details-toggle-full-btn"
              onClick={() => setShowFull(v => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              {showFull ? <><EyeOff className="w-3.5 h-3.5" /> Hide details</> : <><Eye className="w-3.5 h-3.5" /> Show my details</>}
            </button>
            <button
              type="button"
              data-testid="partner-bank-details-edit-btn"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-cyan-600 hover:bg-cyan-700 transition-colors"
            >
              Edit
            </button>
            <button
              type="button"
              data-testid="partner-bank-details-clear-btn"
              onClick={clearAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-rose-700 hover:bg-rose-50 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3">
          <div
            data-testid="partner-bank-details-empty"
            className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
            <div>
              <span className="font-semibold">Not set up yet.</span>{" "}
              Add your bank details so Aware can send your next commission payout.
            </div>
          </div>
          <div className="mt-4">
            <button
              type="button"
              data-testid="partner-bank-details-add-btn"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white bg-cyan-600 hover:bg-cyan-700 transition-colors"
            >
              <Landmark className="w-4 h-4" /> Add bank details
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function BankField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="text-sm text-slate-800 font-mono mt-1">{value || "—"}</div>
    </div>
  );
}

function BankDetailsForm({ form, setForm, onCancel, onSave, saving }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <div data-testid="partner-bank-details-form" className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <FormField
        label="Account name" required
        value={form.account_name}
        onChange={(v) => set("account_name", v)}
        testid="partner-bank-details-input-account-name"
        placeholder="e.g. Pristine Water Solutions Pty Ltd"
        maxLength={100}
      />
      <FormField
        label="BSB" required
        value={form.bsb}
        onChange={(v) => set("bsb", displayBsb(v))}
        testid="partner-bank-details-input-bsb"
        placeholder="123-456"
        maxLength={7}
      />
      <FormField
        label="Account number" required
        value={form.account_number}
        onChange={(v) => set("account_number", (v || "").replace(/\D/g, "").slice(0, 10))}
        testid="partner-bank-details-input-account-number"
        placeholder="12345678"
        maxLength={10}
      />
      <FormField
        label="PayID (optional)"
        value={form.payid}
        onChange={(v) => set("payid", v)}
        testid="partner-bank-details-input-payid"
        placeholder="email, mobile or ABN"
        maxLength={100}
      />
      <FormField
        label="Remittance email"
        value={form.remittance_email}
        onChange={(v) => set("remittance_email", v)}
        testid="partner-bank-details-input-remittance-email"
        placeholder="accounts@yourcompany.com.au"
        maxLength={254}
      />
      <FormField
        label="Notes (optional)"
        value={form.notes}
        onChange={(v) => set("notes", v)}
        testid="partner-bank-details-input-notes"
        placeholder="Anything Aware should know when paying you"
        maxLength={500}
      />
      <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
        <button
          type="button"
          data-testid="partner-bank-details-form-cancel-btn"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 rounded-full text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="partner-bank-details-form-save-btn"
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 transition-colors"
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? "Saving…" : "Save bank details"}
        </button>
      </div>
    </div>
  );
}

function FormField({ label, required, value, onChange, testid, placeholder, maxLength }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </span>
      <input
        type="text"
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none font-mono"
      />
    </label>
  );
}



// ═══════════════════════════════════════════════════════════════════
// Email notification preferences (Feb 2026 · P1)
// ═══════════════════════════════════════════════════════════════════
function PartnerNotificationPrefsSection() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/partner/notification-preferences");
        if (!cancelled) setPrefs(data);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const flip = async (type) => {
    if (!prefs) return;
    const field = `${type}_email`;
    const next = !prefs[type]?.email;
    setSaving(type);
    // Optimistic
    const prev = prefs;
    setPrefs({
      ...prefs,
      [type]: { ...prefs[type], email: next, updated_via: "portal" },
    });
    try {
      const { data } = await api.put("/partner/notification-preferences", { [field]: next });
      setPrefs(data);
      toast.success(
        next
          ? `You'll now receive ${_LABELS[type]}.`
          : `You'll no longer receive ${_LABELS[type]}.`,
      );
    } catch (e) {
      setPrefs(prev);   // rollback
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving("");
    }
  };

  return (
    <section
      data-testid="partner-notification-prefs-section"
      className="rounded-2xl bg-white border border-slate-200 shadow-sm"
    >
      <header className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
        <div className="flex items-start gap-2.5">
          <BellRing className="w-4 h-4 mt-0.5 text-indigo-600" />
          <div>
            <h3 className="text-sm font-bold text-slate-900">Email notifications</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Manage the operational payout emails Aware Water Testing sends you.
              Statements always remain downloadable from{" "}
              <span className="font-semibold">Ledger &amp; Payouts</span> even when emails are off.
            </p>
          </div>
        </div>
      </header>
      <div className="p-5">
        {loading ? (
          <div className="text-sm text-slate-500 py-4">Loading…</div>
        ) : error ? (
          <div
            data-testid="partner-notification-prefs-error"
            className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3"
          >
            {error}
          </div>
        ) : prefs ? (
          <>
            <PrefRow
              type="payout_scheduled"
              label={_LABELS.payout_scheduled}
              help="Heads-up email when we group a set of commission rows into a payout run. No attachment."
              pref={prefs.payout_scheduled}
              saving={saving === "payout_scheduled"}
              onFlip={() => flip("payout_scheduled")}
            />
            <PrefRow
              type="payout_paid"
              label={_LABELS.payout_paid}
              help="Confirmation email when a payout is marked Paid. Attaches your PDF statement."
              pref={prefs.payout_paid}
              saving={saving === "payout_paid"}
              onFlip={() => flip("payout_paid")}
            />
            <PrefRow
              type="customer_report_published"
              label={_LABELS.customer_report_published}
              help="Sent whenever a customer's water test report is approved and emailed to them. Lets you follow up while the results are fresh."
              pref={prefs.customer_report_published}
              saving={saving === "customer_report_published"}
              onFlip={() => flip("customer_report_published")}
            />
            {prefs.customer_report_published?.email !== false && (
              <div className="pl-4 pb-3 border-b border-slate-100 space-y-1.5" data-testid="partner-report-link-prefs">
                <ReportLinkToggle
                  field="customer_report_published_include_summary_link"
                  label="Include a link to the Summary Report"
                  checked={prefs.customer_report_published?.include_summary_link !== false}
                  prefs={prefs} setPrefs={setPrefs}
                />
                <ReportLinkToggle
                  field="customer_report_published_include_coa_link"
                  label="Include a link to the Full Analysis / COA"
                  checked={prefs.customer_report_published?.include_coa_link !== false}
                  prefs={prefs} setPrefs={setPrefs}
                />
              </div>
            )}
            <div className="mt-3 text-[10px] text-slate-400">
              Last changed:{" "}
              {prefs.updated_at
                ? new Date(prefs.updated_at).toLocaleString("en-AU")
                : "—"}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}


const _LABELS = {
  payout_scheduled:          "Payout scheduled emails",
  payout_paid:               "Payout paid emails",
  customer_report_published: "Email me when a customer's results are released",
};


// Jun 2026 — report-link sub-toggles for the results-released email.
function ReportLinkToggle({ field, label, checked, prefs, setPrefs }) {
  const key = field.includes("summary") ? "include_summary_link" : "include_coa_link";
  const onChange = async (e) => {
    const next = e.target.checked;
    const prev = prefs;
    setPrefs({
      ...prefs,
      customer_report_published: { ...prefs.customer_report_published, [key]: next },
    });
    try {
      const { data } = await api.put("/partner/notification-preferences", { [field]: next });
      setPrefs(data);
    } catch (err) {
      setPrefs(prev);
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
      <input type="checkbox" data-testid={`partner-pref-${field}`} checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}


function PrefRow({ type, label, help, pref, saving, onFlip }) {
  const on = !!pref?.email;
  return (
    <div
      data-testid={`partner-notif-pref-row-${type}`}
      className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11.5px] text-slate-500 mt-0.5 leading-snug">{help}</div>
        {pref?.updated_via === "unsubscribe_link" && !on && (
          <div className="mt-1 text-[10.5px] text-amber-700">
            Turned off via an unsubscribe link.
          </div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        onClick={onFlip}
        disabled={saving}
        data-testid={`partner-notif-pref-toggle-${type}`}
        className={[
          "shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1",
          on ? "bg-emerald-500" : "bg-slate-300",
          saving ? "opacity-60 cursor-wait" : "cursor-pointer",
        ].join(" ")}
      >
        <span
          className={[
            "inline-block h-4 w-4 transform rounded-full bg-white shadow ring-1 ring-black/5 transition-transform",
            on ? "translate-x-6" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
}
